import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useKiosk } from '../hooks/useKiosk';
import { AVATARS } from '../components/JoinScreen';

function avatarEmoji(avatarId) {
  return AVATARS.find((a) => a.id === avatarId)?.emoji || '👤';
}

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;
const MAX_UPCOMING = 8;
const MUTE_STORAGE_KEY_PREFIX = 'vq_display_muted_';

function loadMuted(shopId) {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY_PREFIX + shopId) === '1';
  } catch {
    return false;
  }
}

function saveMuted(shopId, muted) {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY_PREFIX + shopId, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * TTS helper. Speaks a message in the current i18n language. Silently
 * no-ops when the browser lacks speechSynthesis, when muted, or when
 * TTS is disabled at the shop level.
 */
function speak(text, lang) {
  if (!text) return;
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try {
    const utter = new window.SpeechSynthesisUtterance(text);
    if (lang) utter.lang = lang;
    utter.rate = 1.0;
    utter.volume = 1.0;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore — TTS is best-effort */
  }
}

function langToBcp47(lang) {
  if (!lang) return 'en-US';
  const base = lang.split('-')[0].toLowerCase();
  const map = { en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', it: 'it-IT' };
  return map[base] || lang;
}

export default function DisplayPage() {
  const { t, i18n } = useTranslation();
  const { shopId } = useParams();
  const [searchParams] = useSearchParams();
  const orientation = searchParams.get('orientation') === 'portrait' ? 'portrait' : 'landscape';

  const { queues, calledTicket, calledTickets, wsStatus } = useKiosk(shopId);
  const [shopName, setShopName] = useState('');
  const [services, setServices] = useState([]);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [ttsUnlocked, setTtsUnlocked] = useState(false);
  const [muted, setMuted] = useState(() => loadMuted(shopId));
  const [time, setTime] = useState(new Date());

  const lastAnnouncedRef = useRef(null);

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_BASE}/api/v1/public/shops/by-id/${shopId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setShopName(d.name || '');
          setTtsEnabled(d.ttsEnabled !== false);
        }
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/v1/public/shops/${shopId}/services`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setServices(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [shopId]);

  // Clock tick
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // TTS announcement when a new ticket is called. The display feed is
  // anonymized (ticketUuid is blank), so dedup on the public ticket number +
  // call timestamp — keying on ticketUuid would be "" and either never fire or
  // never re-fire.
  useEffect(() => {
    if (!calledTicket?.ticketNumber) return;
    const callId = `${calledTicket.ticketNumber}:${calledTicket.calledAtMs || ''}`;
    if (lastAnnouncedRef.current === callId) return;
    lastAnnouncedRef.current = callId;

    if (!ttsEnabled || muted || !ttsUnlocked) return;

    const number = calledTicket.ticketNumber;
    const desk = calledTicket.deskLabel;
    const phrase = desk
      ? t('display.ttsCalledWithDesk', { number: number || '', desk })
      : t('display.ttsCalledNoDesk', { number: number || '' });
    speak(phrase, langToBcp47(i18n.language));
  }, [calledTicket, ttsEnabled, muted, ttsUnlocked, t, i18n.language]);

  // Cancel any queued speech on unmount or mute.
  useEffect(() => {
    if (muted && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [muted]);

  useEffect(() => () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      saveMuted(shopId, next);
      return next;
    });
  };

  // Per-service rows: one card per queue with its "now serving" + upcoming.
  // Falls back to a single '_default' row for single-queue shops.
  const queueRows = useMemo(() => {
    const serviceMap = Object.fromEntries(services.map((s) => [s.id, s.name]));
    const keys = Object.keys(queues);
    if (keys.length === 0) keys.push('_default');
    return keys.map((svcId) => {
      const called = calledTickets[svcId] || null;
      // Exclude the currently-served ticket from "up next" by ticket NUMBER.
      // The display gets an anonymized feed where every ticketUuid is blank, so
      // matching on ticketUuid would treat all entries as "the called one" and
      // empty the upcoming list whenever someone is being served.
      const svcEntries = (queues[svcId] || []).filter(
        (e) => !called || !called.ticketNumber || e.ticketNumber !== called.ticketNumber,
      );
      return {
        svcId,
        serviceName: svcId === '_default' ? '' : (serviceMap[svcId] || ''),
        called,
        upcoming: svcEntries.slice(0, MAX_UPCOMING),
      };
    });
  }, [queues, calledTickets, services]);

  const multiService = queueRows.length > 1 || (queueRows.length === 1 && queueRows[0].svcId !== '_default');

  return (
    <>
      <style>{CSS}</style>
      <div className={`display display-${orientation}`} aria-live="polite">
        {/* Header */}
        <header className="display-header">
          <div className="display-brand">
            <span className="display-logo">vQueue</span>
            {shopName && <span className="display-shop">{shopName}</span>}
          </div>
          <div className="display-meta">
            <span className="display-time">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className={`display-status ${wsStatus === 'connected' ? 'online' : 'offline'}`}>
              {wsStatus === 'connected' ? t('display.live') : t('display.connecting')}
            </span>
          </div>
        </header>

        {/* Per-queue rows: each service gets its own "now serving" + upcoming */}
        <section className="display-rows" aria-label={t('display.nowServing')}>
          {queueRows.map((row) => {
            const number = row.called?.ticketNumber;
            const desk = row.called?.deskLabel;
            const where = desk ? t('display.onDesk', { desk }) : t('display.atCounter');
            return (
              <article key={row.svcId} className="display-row">
                {multiService && row.serviceName && (
                  <h2 className="display-row-service">{row.serviceName}</h2>
                )}
                <div className="display-row-serving">
                  {row.called ? (
                    <>
                      <span className="display-row-label">{t('display.nowServing')}</span>
                      <span className="display-row-number">#{number || '—'}</span>
                      <span className="display-row-where">{where}</span>
                    </>
                  ) : (
                    <>
                      <span className="display-row-label">{t('display.nowServing')}</span>
                      <span className="display-row-number display-row-idle">—</span>
                      <span className="display-row-where">{t('display.waitingForNext')}</span>
                    </>
                  )}
                </div>
                <div className="display-row-upcoming">
                  <span className="display-upcoming-title">{t('display.upcoming')}</span>
                  {row.upcoming.length === 0 ? (
                    <span className="display-upcoming-empty">{t('display.noUpcoming')}</span>
                  ) : (
                    <ol className="display-upcoming-list">
                      {row.upcoming.map((entry, idx) => (
                        <li key={entry.ticketNumber || entry.position || idx} className="display-upcoming-item">
                          <span className="display-upcoming-avatar">{avatarEmoji(entry.avatarId)}</span>
                          <span className="display-upcoming-num">#{entry.ticketNumber || entry.position}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </article>
            );
          })}
        </section>

        {/* TTS unlock prompt — browsers require user interaction before speech */}
        {ttsEnabled && !ttsUnlocked && (
          <button
            type="button"
            className="display-tts-unlock"
            onClick={() => {
              setTtsUnlocked(true);
              speak(t('display.ttsReady'), langToBcp47(i18n.language));
            }}
          >
            {t('display.enableSound')}
          </button>
        )}

        {/* Mute toggle (corner, password-free) */}
        {ttsEnabled && ttsUnlocked && (
          <button
            type="button"
            className="display-mute"
            onClick={toggleMute}
            aria-pressed={muted}
            title={muted ? t('display.unmute') : t('display.mute')}
          >
            {muted ? '🔇' : '🔊'}
          </button>
        )}
      </div>
    </>
  );
}

const CSS = `
  .display {
    min-height: 100dvh; background: #0F172A; color: #F8FAFC;
    font-family: 'Inter', system-ui, sans-serif;
    display: flex; flex-direction: column;
    overflow: hidden;
  }
  .display-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 1.25rem 2.5rem; border-bottom: 1px solid #1E293B;
    flex: 0 0 auto;
  }
  .display-brand { display: flex; align-items: baseline; gap: 1.25rem; }
  .display-logo {
    font-size: 1.75rem; font-weight: 800;
    background: linear-gradient(135deg, #7C3AED, #06B6D4);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .display-shop { font-size: 1.35rem; font-weight: 600; color: #CBD5E1; }
  .display-meta { display: flex; align-items: center; gap: 1.25rem; }
  .display-time {
    font-size: 2rem; font-weight: 700; color: #E2E8F0;
    font-variant-numeric: tabular-nums;
  }
  .display-status {
    font-size: 0.8rem; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; letter-spacing: 0.1em;
  }
  .display-status.online { background: #34D399; color: #0F172A; }
  .display-status.offline { background: #F59E0B; color: #0F172A; }

  @keyframes display-pulse {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.15); }
  }

  .display-rows {
    flex: 1; display: flex; flex-direction: column;
    gap: 1.25rem; padding: 1.5rem 2.5rem 2rem;
    overflow-y: auto;
  }
  .display-row {
    background: #0B1220; border: 1px solid #1E293B; border-radius: 16px;
    padding: 1.5rem 2rem;
    display: flex; flex-direction: column; gap: 1rem;
  }
  .display-row-service {
    margin: 0; font-size: clamp(1.25rem, 2vw, 1.75rem);
    font-weight: 800; color: #A78BFA; letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  .display-row-serving {
    display: flex; flex-wrap: wrap; align-items: baseline;
    gap: 0.75rem 1.5rem;
  }
  .display-row-label {
    font-size: 0.95rem; font-weight: 800; letter-spacing: 0.3em;
    text-transform: uppercase; color: #94A3B8;
  }
  .display-row-number {
    font-size: clamp(4rem, 10vw, 8rem);
    font-weight: 900; line-height: 1;
    background: linear-gradient(135deg, #7C3AED, #06B6D4);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    font-variant-numeric: tabular-nums;
    animation: display-pulse 3s ease-in-out infinite;
  }
  .display-row-idle {
    background: none; -webkit-text-fill-color: #334155; color: #334155;
    animation: none;
  }
  .display-row-where {
    font-size: clamp(1.25rem, 2.2vw, 2rem);
    font-weight: 700; color: #E2E8F0;
  }

  .display-row-upcoming {
    display: flex; flex-direction: column; gap: 0.5rem;
    border-top: 1px solid #1E293B; padding-top: 0.75rem;
  }
  .display-upcoming-title {
    font-size: 0.85rem; font-weight: 700; letter-spacing: 0.2em;
    text-transform: uppercase; color: #64748B;
  }
  .display-upcoming-empty { color: #475569; font-size: 1.1rem; }
  .display-upcoming-list {
    list-style: none; padding: 0; margin: 0;
    display: flex; gap: 0.6rem; flex-wrap: wrap;
  }
  .display-upcoming-item {
    background: #1E293B; border: 1px solid #334155; border-radius: 10px;
    padding: 0.5rem 0.9rem; display: flex; align-items: center; gap: 0.4rem;
  }
  .display-upcoming-avatar { font-size: 1.4rem; line-height: 1; }
  .display-upcoming-num {
    font-size: clamp(1.25rem, 2vw, 1.75rem);
    font-weight: 800; color: #06B6D4;
    font-variant-numeric: tabular-nums;
  }

  .display-tts-unlock {
    position: fixed; bottom: 1.25rem; left: 50%; transform: translateX(-50%);
    padding: 0.75rem 2rem; border-radius: 10px; border: 1px solid #7C3AED;
    background: rgba(124,58,237,0.15); color: #A78BFA; font-size: 1rem;
    font-weight: 600; cursor: pointer; backdrop-filter: blur(8px);
    animation: vq-glow-pulse 2s ease-in-out infinite;
  }
  @keyframes vq-glow-pulse {
    0%, 100% { box-shadow: 0 0 12px rgba(124,58,237,0.2); }
    50% { box-shadow: 0 0 24px rgba(124,58,237,0.4); }
  }

  .display-mute {
    position: fixed; bottom: 1.25rem; right: 1.25rem;
    width: 48px; height: 48px; border-radius: 50%;
    background: rgba(30,41,59,0.6); border: 1px solid #334155;
    color: #E2E8F0; font-size: 1.25rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(8px);
    transition: background 0.2s, transform 0.1s;
  }
  .display-mute:hover { background: rgba(30,41,59,0.9); }
  .display-mute:active { transform: scale(0.95); }

  /* Portrait fallback */
  .display-portrait .display-row-number { font-size: clamp(3.5rem, 14vw, 7rem); }
  .display-portrait .display-upcoming-item { padding: 0.4rem 0.75rem; }

  @media (max-width: 768px) {
    .display-header { padding: 1rem 1.25rem; }
    .display-time { font-size: 1.5rem; }
    .display-rows { padding: 1rem 1.25rem 1.5rem; gap: 1rem; }
    .display-row { padding: 1rem 1.25rem; }
  }
`;
